// ModalPage.jsx
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { FaPhoneAlt, FaFileAlt } from "react-icons/fa";
import InquiryForm from "./EnquiryForm";
import { getImageUrl, getApiUrl } from "../utils/api";
import "./design/ModalPage.css";

// --- UTILITY FUNCTIONS ---
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const formatIndianCurrency = (number) => {
  if (!number || isNaN(number)) return "—";
  if (number >= 10000000) return `₹ ${(number / 10000000).toFixed(1)} cr`;
  if (number >= 100000) return `₹ ${Math.round(number / 100000)} lacs`;
  return `₹ ${number.toLocaleString("en-IN")}`;
};

// --- COMPONENT ---
const ModalPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMobileForm, setShowMobileForm] = useState(false);
  const [imageErrors, setImageErrors] = useState({});

  // Get model ID from URL
  const searchParams = new URLSearchParams(location.search);
  const modelId = parseInt(searchParams.get('id')) || 0;

  // Map model IDs to names
  const modelTypeMap = {
    1: "FOFO",
    2: "FOCO",
    3: "COCO",
    4: "Both"
  };

  const modelDescriptions = {
    1: "You invest, own, and operate — brand guides, you run the show.",
    2: "You fund it, but the company operates — lower effort, shared returns.",
    3: "Co-branding model — shared ownership and operations.",
    4: "A hybrid model — shared control and profits between owner and company."
  };

  const modelType = modelTypeMap[modelId] || "Unknown";
  const modelDescription = modelDescriptions[modelId] || "";

  const apiUrl = getApiUrl(`get-brands-by-model.php?id=${modelId}`);

  // Add unique page identifier to body when component mounts
  useEffect(() => {
    document.body.setAttribute('data-mp-page', 'active');
    return () => {
      document.body.removeAttribute('data-mp-page');
    };
  }, []);

  useEffect(() => {
    const fetchBrands = async () => {
      setLoading(true);
      try {
        const res = await fetch(apiUrl);
        if (!res.ok) throw new Error('Network response was not ok');
        const data = await res.json();
        
        if (data.success && Array.isArray(data.brands)) {
          const randomizedBrands = shuffleArray(data.brands);
          setBrands(randomizedBrands);
          setError(null);
        } else {
          setBrands([]);
          setError(data.message || 'No brands found for this model type.');
        }
      } catch (err) {
        console.error("Error fetching brands:", err);
        setError("Failed to fetch brands. Please try again.");
        setBrands([]);
      } finally {
        setLoading(false);
      }
    };
    if (modelId) fetchBrands();
  }, [modelId, apiUrl]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showMobileForm) {
      document.body.classList.add('mp-modal-open');
    } else {
      document.body.classList.remove('mp-modal-open');
    }
    return () => document.body.classList.remove('mp-modal-open');
  }, [showMobileForm]);

  const handleKnowMore = (register_id) => {
    navigate(`/brand/${register_id}`);
  };

  const handleImageError = (brandId) => {
    setImageErrors(prev => ({ ...prev, [brandId]: true }));
  };

  const getModelTitle = () => {
    switch(modelId) {
      case 1: return "FOFO Franchises";
      case 2: return "FOCO Franchises";
      case 3: return "COCO Franchises";
      case 4: return "Both Model Franchises";
      default: return "Franchises";
    }
  };

  if (loading) return <div className="mp-loading-message"><div className="mp-loading-spinner"></div> Loading {modelType} brands...</div>;
  if (error) return <div className="mp-error-message">{error}</div>;
  if (brands.length === 0) return <div className="mp-no-brands-message">No franchises found for {modelType} model type.</div>;

  return (
    <>
      <div className="mp-container">
        <div className="mp-main">
          <div className="mp-header">
            <h2 className="mp-heading">
              {getModelTitle()}
            </h2>
            <p className="mp-description">{modelDescription}</p>
            <div className="mp-badge">{modelType}</div>
            <p className="mp-count">{brands.length} franchise{brands.length !== 1 ? 's' : ''} available</p>
          </div>

          <div className="mp-grid">
            {brands.map((brand, i) => (
              <div
                className="mp-card"
                key={brand.register_id || i}
              >
                <div className="mp-img-wrap">
                  <a href={`/brand/${brand.register_id}`} onClick={(e) => { e.preventDefault(); handleKnowMore(brand.register_id); }}>
                    <img
                      src={!imageErrors[brand.register_id] ? getImageUrl(brand.logo) : 'https://via.placeholder.com/200?text=No+Image'}
                      alt={brand.name || 'Brand'}
                      onError={() => handleImageError(brand.register_id)}
                      loading="lazy"
                    />
                  </a>
                </div>
                <div className="mp-category">{brand.sector || "—"}</div>
                <div className="mp-content">
                  <h3 className="mp-brand-name">{brand.name || 'Unknown Brand'}</h3>
                  <div className="mp-detail">
                    <div className="mp-biz-field">
                      <span className="mp-label">Investment:</span>
                      <span className="mp-value">
                        {formatIndianCurrency(brand.min_investment)} - {formatIndianCurrency(brand.max_investment)}
                      </span>
                    </div>
                    <div className="mp-biz-field">
                      <span className="mp-label">Area:</span>
                      <span className="mp-value">{brand.min_area || 0} - {brand.max_area || 0} sq.ft</span>
                    </div>
                    <div className="mp-biz-field">
                      <span className="mp-label">Outlets:</span>
                      <span className="mp-value">{brand.total_outlets || 0}</span>
                    </div>
                    {brand.modal_name && (
                      <div className="mp-biz-field">
                        <span className="mp-label">Model:</span>
                        <span className="mp-value mp-model-type">{brand.modal_name}</span>
                      </div>
                    )}
                  </div>

                  <div className="mp-buttons-row">
                    <button 
                      className="mp-know-more-btn" 
                      onClick={(e) => { e.stopPropagation(); handleKnowMore(brand.register_id); }}
                      type="button">
                      Know More
                    </button>
                    
                    <button
                      className="mp-call-btn"
                      onClick={(e) => {
                        e.stopPropagation(); 
                        window.location.href = 'tel:+919403890794'; 
                      }}
                      type="button"
                    >
                      <FaPhoneAlt style={{ marginRight: "6px" }} />
                      Call Us
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Desktop Sidebar - Hidden on mobile */}
        <div className="mp-sidebar">
          <InquiryForm />
        </div>
      </div>

      {/* Mobile Floating Instant Apply Button - BOTTOM CENTER */}
      <button 
        className="mp-instant-apply-btn"
        onClick={() => setShowMobileForm(true)}
      >
        <FaFileAlt /> Instant Apply
      </button>

      {/* Mobile Form Overlay */}
      {showMobileForm && (
        <div className="mp-form-overlay" onClick={() => setShowMobileForm(false)}>
          <div className="mp-form-overlay-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="mp-close-form-btn" 
              onClick={() => setShowMobileForm(false)}
            >
              ✕
            </button>
            <InquiryForm />
          </div>
        </div>
      )}
    </>
  );
};

export default ModalPage;